import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import type { ConversationRow, MessageRow } from "./types";

/**
 * Inbox of all open conversations for the current member.
 * Hydrates the "other" member profile + a 1-line preview + unread flag.
 */
export const useConversations = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<ConversationRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setConversations([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("conversations")
      .select("*")
      .is("archived_at", null)
      .order("last_message_at", { ascending: false, nullsFirst: false });
    if (error) {
      setConversations([]);
      setLoading(false);
      return;
    }
    const rows = (data as ConversationRow[]) ?? [];
    if (!rows.length) {
      setConversations([]);
      setLoading(false);
      return;
    }

    const otherIds = Array.from(
      new Set(rows.map((r) => (r.member_a === user.id ? r.member_b : r.member_a))),
    );
    const { data: profs } = await supabase
      .from("public_profiles")
      .select("id, display_name, photos, profile_id")
      .in("id", otherIds);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const byId = new Map((profs as any[] | null)?.map((p) => [p.id, p]) ?? []);

    // batch-load latest message per conv (simple approach: one query, take latest per conv)
    const convIds = rows.map((r) => r.id);
    const { data: msgs } = await supabase
      .from("messages")
      .select("conversation_id, body, kind, created_at, sender_id")
      .in("conversation_id", convIds)
      .order("created_at", { ascending: false })
      .limit(200);
    const previewByConv = new Map<string, { body: string | null; kind: string }>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (msgs as any[] | null)?.forEach((m) => {
      if (!previewByConv.has(m.conversation_id)) {
        previewByConv.set(m.conversation_id, { body: m.body, kind: m.kind });
      }
    });

    rows.forEach((r) => {
      const otherId = r.member_a === user.id ? r.member_b : r.member_a;
      r.other = byId.get(otherId);
      const lastReadAt = r.member_a === user.id ? r.a_last_read_at : r.b_last_read_at;
      r.unread =
        !!r.last_message_at &&
        (!lastReadAt || new Date(r.last_message_at) > new Date(lastReadAt));
      const p = previewByConv.get(r.id);
      r.preview = p
        ? p.kind === "voice"
          ? "🎙 Voice note"
          : p.kind === "image"
            ? "📷 Photo"
            : p.body
        : null;
    });

    setConversations(rows);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  // realtime: refresh inbox when any new message lands
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`inbox:${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages" },
        () => {
          void load();
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user, load]);

  const openFromMatch = useCallback(async (matchId: string) => {
    const { data, error } = await supabase.rpc("fn_open_conversation", {
      _match_id: matchId,
    });
    if (error) return { id: null as string | null, error: error.message };
    return { id: data as string, error: null };
  }, []);

  return { conversations, loading, reload: load, openFromMatch };
};

export type { MessageRow };
